import { Dispatch, Fragment } from 'react';
import {
	ColumnDef,
	ExpandedState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable
} from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	isLoading?: boolean;
	expanded?: ExpandedState;
	setExpanded?: Dispatch<React.SetStateAction<ExpandedState>>;
	getExpandedRowContent?: (row: TData) => React.ReactNode;
}

export function DataTable<TData, TValue>({
	columns,
	data,
	isLoading,
	getExpandedRowContent,
	expanded,
	setExpanded
}: DataTableProps<TData, TValue>) {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onExpandedChange: setExpanded,
		state: {
			expanded
		}
	});

	return (
		<div className={cn('rounded-md border', isLoading && 'opacity-50 pointer-events-none')}>
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map(headerGroup => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map(header => {
								return (
									<TableHead key={header.id} className="px-8">
										{header.isPlaceholder
											? null
											: flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map(row => (
							<Fragment key={row.id}>
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
									className={cn({
										'border-b-0 bg-slate-100/50 dark:bg-slate-800/50': row.getIsExpanded()
									})}
								>
									{row.getVisibleCells().map(cell => (
										<TableCell key={cell.id} className="px-8">
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
								{row.getIsExpanded() && getExpandedRowContent && (
									<TableRow key={`${row.id}-expanded`}>
										<TableCell colSpan={columns.length} className="px-8" key={`${row.id}-expanded`}>
											{getExpandedRowContent(row.original)}
										</TableCell>
									</TableRow>
								)}
							</Fragment>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								No results.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}
